// @flow
import React from 'react';
import { sprintf } from 'sprintf-js';
import type { CurrentUser } from 'api/currentUser';
import Form, {
  InputField,
  SelectField,
  TextAreaField,
  SubmitButton,
  RecaptchaField,
  MultiInputField,
  FileUploadField,
  Section
} from 'components/Form';
import { BodySmall, H3 } from 'components/Text';
import Box from 'components/Box';
import { postJson } from 'utils/http';
import { LOCALES } from 'utils/constants';
import { resetRecaptcha } from 'utils/recaptcha';

const { useEffect, useState } = React;

type Reason = {
  id: string,
  text: string,
  subReasons?: Array<Reason>
};

const ATTACHMENTS_MAX_LENGTH = 5;

const handleSubmit = (
  { handleError, updateSubmitting, values },
  blockId,
  currentUser,
  setSubmitted
) => {
  const { orgs = [] } = currentUser || {};
  const { id: orgId = '' } =
    orgs.find(org => org.name === values.orgName) || {};
  values = {
    orgId,
    ...values,
    attachmentIds: values.attachmentIds.filter(Boolean)
  };
  postJson(`/data/website/support-contact-form/${blockId}`, values)
    .then(() => {
      updateSubmitting(false);
      setSubmitted(true);
    })
    .catch(e => {
      resetRecaptcha();
      handleError(e, {
        recaptchaKey: values.recaptchaKey + 1,
        gRecaptchaResponse: undefined
      });
    });
};

const SupportContactForm = ({
  currentUser,
  reasons,
  selectedReason,
  selectedSubReason,
  updateValue
}: {
  currentUser: ?CurrentUser,
  reasons: Array<Reason>,
  selectedReason: ?Reason,
  selectedSubReason: ?Reason,
  updateValue: Function
}) => {
  const [subReasons, setSubReasons] = useState([]);
  const [subSubReasons, setSubSubReasons] = useState([]);
  useEffect(
    () => {
      if (selectedReason) {
        setSubReasons(selectedReason.subReasons || []);

        if (selectedReason.id === 'ORG' && selectedSubReason) {
          setSubSubReasons(selectedSubReason.subReasons || []);
        } else {
          setSubSubReasons([]);
          updateValue('subSubReason', undefined);
        }
      }
    },
    [selectedReason, selectedSubReason, updateValue]
  );
  return (
    <>
      <InputField label={`${getText('Email to contact you')}*`} name="email" />
      <InputField label={`${getText('First Name')}*`} name="firstName" />
      <InputField label={`${getText('Last name')}*`} name="lastName" />
      <SelectField
        label={`${getText('Your language')}*`}
        name="locale"
        options={LOCALES.map(locale => ({
          label: locale.name,
          value: locale.id
        }))}
      />
      {currentUser && currentUser.orgs && currentUser.orgs.length > 0 ? (
        <>
          <SelectField
            label={`${getText('Which organization is this about?')}*`}
            name="orgName"
            options={[
              {
                label: `${getText('This is not about an organization')}`,
                value: ''
              }
            ].concat(
              currentUser.orgs.map(org => ({
                label: org.name,
                value: org.name
              }))
            )}
          />
        </>
      ) : (
        <InputField
          label={`${getText(
            'Which organization is this regarding (if any)?'
          )}*`}
          name="orgName"
        />
      )}
      <SelectField
        label={`${getText('What is this about?')}*`}
        name="reason"
        options={[{ label: `${getText('Please Select')}`, value: '' }].concat(
          reasons.map(reason => ({
            label: reason.text,
            value: reason.id
          }))
        )}
      />
      {subReasons.length > 0 && (
        <SelectField
          label={`${getText('Narrow it down')}*`}
          name="subReason"
          options={[{ label: `${getText('Please select')}`, value: '' }].concat(
            subReasons.map(subReason => ({
              label: subReason.text,
              value: subReason.id
            }))
          )}
        />
      )}
      {subSubReasons.length > 0 && (
        <SelectField
          label={`${getText('Narrow it down')}*`}
          name="subSubReason"
          options={[{ label: `${getText('Please select')}`, value: '' }].concat(
            subSubReasons.map(subSubReason => ({
              label: subSubReason.text,
              value: subSubReason.id
            }))
          )}
        />
      )}
      <TextAreaField label={`${getText('How can we help?')}`} name="message" />
      <MultiInputField
        hideControls
        component={FileUploadField}
        maxInputs={ATTACHMENTS_MAX_LENGTH}
        name="attachmentIds"
        label={getText('Attachments')}
        description={sprintf(getText('Upload up to %(count)s attachments'), {
          count: ATTACHMENTS_MAX_LENGTH
        })}
        extraData={new Array(ATTACHMENTS_MAX_LENGTH).fill({
          downloadAttachment: 'false',
          fileType: 'CONTACT_FORM',
          isRequired: false,
          label: ''
        })}
        showEmpty
      />
      <RecaptchaField />

      <Box mt={64}>
        <SubmitButton size="large" width={1}>
          {getText('Submit')}
        </SubmitButton>
      </Box>
    </>
  );
};

const SupportContactFormBlock = ({
  blockId,
  currentUser,
  data
}: {
  blockId: string,
  currentUser: ?CurrentUser,
  data: { reasons: Array<Reason> }
}) => {
  const [submitted, setSubmitted] = useState(false);
  const { reasons } = data;
  return submitted ? (
    <>
      <H3>{getText('Thanks for submitting')}</H3>
      <BodySmall>{getText('Someone will respond to you shortly.')}</BodySmall>
    </>
  ) : (
    <Section>
      <Form
        data-qa-id="support-contact-form"
        initialValues={{
          email: currentUser && currentUser.email,
          firstName: currentUser && currentUser.firstName,
          lastName: currentUser && currentUser.lastName,
          locale: USER_LOCALE,
          attachmentIds: ['']
        }}
        onSubmit={formContext =>
          handleSubmit(formContext, blockId, currentUser, setSubmitted)
        }
        render={({ getValue, updateValue }) => {
          const selectedReason = reasons.find(r => r.id === getValue('reason'));
          const selectedSubReason =
            selectedReason &&
            selectedReason.subReasons &&
            selectedReason.subReasons.find(s => s.id === getValue('subReason'));
          return (
            <SupportContactForm
              blockId={blockId}
              currentUser={currentUser}
              reasons={reasons}
              selectedReason={selectedReason}
              selectedSubReason={selectedSubReason}
              updateValue={updateValue}
            />
          );
        }}
      />
    </Section>
  );
};

export default SupportContactFormBlock;
